import { cn } from '#app/utils/misc.tsx'

/**
 * StatRow - Animated statistics display
 * Usage in MDX: <StatRow stats={[{value: "99.5%", label: "Effectiveness", source: "Study"}]} />
 */
export function StatRow({
	stats,
}: {
	stats: Array<{ value: string; label: string; source?: string }>
}) {
	return (
		<div className="my-8 grid gap-6 sm:grid-cols-3">
			{stats.map((stat, i) => (
				<div
					key={i}
					className="rounded-lg border border-stone-200 bg-white p-6 text-center"
				>
					<div className="mb-2 font-serif text-3xl font-medium text-purple-700">
						{stat.value}
					</div>
					<div className="text-sm text-stone-600">{stat.label}</div>
					{stat.source && (
						<div className="mt-1 text-xs text-stone-400">{stat.source}</div>
					)}
				</div>
			))}
		</div>
	)
}

/**
 * Callout - Highlighted information boxes
 * Variants: tip, warning, info, privacy
 */
export function Callout({
	variant = 'info',
	title,
	children,
}: {
	variant?: 'tip' | 'warning' | 'info' | 'privacy'
	title?: string
	children: React.ReactNode
}) {
	const styles = {
		tip: {
			container: 'border-green-200 bg-green-50',
			title: 'text-green-800',
			content: 'text-green-700',
			icon: '💡',
		},
		warning: {
			container: 'border-amber-200 bg-amber-50',
			title: 'text-amber-800',
			content: 'text-amber-700',
			icon: '⚠️',
		},
		info: {
			container: 'border-blue-200 bg-blue-50',
			title: 'text-blue-800',
			content: 'text-blue-700',
			icon: 'ℹ️',
		},
		privacy: {
			container: 'border-purple-200 bg-purple-50',
			title: 'text-purple-800',
			content: 'text-purple-700',
			icon: '🔒',
		},
	}

	const style = styles[variant]

	return (
		<div
			className={cn(
				'my-6 rounded-lg border p-5',
				style.container,
			)}
		>
			{title && (
				<div className={cn('mb-2 flex items-center gap-2 font-medium', style.title)}>
					<span>{style.icon}</span>
					<span>{title}</span>
				</div>
			)}
			<div className={cn('text-sm leading-relaxed', style.content)}>
				{children}
			</div>
		</div>
	)
}

/**
 * ProcessFlow - Step-by-step diagram
 */
export function ProcessFlow({
	title,
	steps,
}: {
	title?: string
	steps: Array<{ title: string; description: string }>
}) {
	return (
		<div className="my-8">
			{title && (
				<h4 className="mb-4 font-medium text-stone-900">{title}</h4>
			)}
			<div className="space-y-4">
				{steps.map((step, i) => (
					<div key={i} className="flex gap-4">
						<div className="flex flex-col items-center">
							<div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-purple-100 text-sm font-medium text-purple-700">
								{i + 1}
							</div>
							{i < steps.length - 1 && (
								<div className="mt-2 h-full w-px bg-purple-200" />
							)}
						</div>
						<div className="pb-6">
							<div className="font-medium text-stone-900">{step.title}</div>
							<div className="mt-1 text-sm text-stone-600">
								{step.description}
							</div>
						</div>
					</div>
				))}
			</div>
		</div>
	)
}

/**
 * ComparisonCard - Before/after or vs. comparisons
 */
export function ComparisonCard({
	title,
	before,
	after,
}: {
	title?: string
	before: { label: string; points: string[] }
	after: { label: string; points: string[] }
}) {
	return (
		<div className="my-8">
			{title && (
				<h4 className="mb-4 font-medium text-stone-900">{title}</h4>
			)}
			<div className="grid gap-4 sm:grid-cols-2">
				{/* Before/Left side */}
				<div className="rounded-lg border border-stone-200 bg-stone-50 p-5">
					<div className="mb-3 font-medium text-stone-700">{before.label}</div>
					<ul className="space-y-2">
						{before.points.map((point, i) => (
							<li key={i} className="flex items-start gap-2 text-sm text-stone-600">
								<span className="mt-1 text-stone-400">✕</span>
								<span>{point}</span>
							</li>
						))}
					</ul>
				</div>

				{/* After/Right side */}
				<div className="rounded-lg border border-purple-200 bg-purple-50 p-5">
					<div className="mb-3 font-medium text-purple-800">{after.label}</div>
					<ul className="space-y-2">
						{after.points.map((point, i) => (
							<li key={i} className="flex items-start gap-2 text-sm text-purple-700">
								<span className="mt-1 text-purple-500">✓</span>
								<span>{point}</span>
							</li>
						))}
					</ul>
				</div>
			</div>
		</div>
	)
}

/**
 * Quote - Pull quote for key insights
 */
export function Quote({
	children,
	author,
	source,
}: {
	children: React.ReactNode
	author?: string
	source?: string
}) {
	return (
		<figure className="my-8 border-l-4 border-purple-300 bg-stone-50 py-4 pl-6 pr-4">
			<blockquote className="font-serif text-lg italic text-stone-700">
				{children}
			</blockquote>
			{(author || source) && (
				<figcaption className="mt-3 text-sm text-stone-500">
					{author && <span className="font-medium">{author}</span>}
					{author && source && <span>, </span>}
					{source && <cite>{source}</cite>}
				</figcaption>
			)}
		</figure>
	)
}

/**
 * MetricBars - Horizontal progress/comparison bars
 */
export function MetricBars({
	title,
	metrics,
	maxValue,
}: {
	title?: string
	metrics: Array<{ label: string; value: number; unit?: string }>
	maxValue?: number
}) {
	const max = maxValue ?? Math.max(...metrics.map((m) => m.value))

	return (
		<div className="my-8">
			{title && (
				<h4 className="mb-4 font-medium text-stone-900">{title}</h4>
			)}
			<div className="space-y-4">
				{metrics.map((metric, i) => (
					<div key={i}>
						<div className="mb-1 flex justify-between text-sm">
							<span className="text-stone-700">{metric.label}</span>
							<span className="font-medium text-stone-900">
								{metric.value}
								{metric.unit}
							</span>
						</div>
						<div className="h-2 overflow-hidden rounded-full bg-stone-100">
							<div
								className="h-full rounded-full bg-purple-500 transition-all duration-500"
								style={{ width: `${(metric.value / max) * 100}%` }}
							/>
						</div>
					</div>
				))}
			</div>
		</div>
	)
}

/**
 * DataTable - Simple comparison table
 */
export function DataTable({
	headers,
	rows,
}: {
	headers: string[]
	rows: string[][]
}) {
	return (
		<div className="my-8 overflow-x-auto">
			<table className="w-full border-collapse text-sm">
				<thead>
					<tr>
						{headers.map((header, i) => (
							<th
								key={i}
								className="border-b border-stone-200 bg-stone-50 px-4 py-3 text-left font-medium text-stone-900"
							>
								{header}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{rows.map((row, i) => (
						<tr key={i}>
							{row.map((cell, j) => (
								<td
									key={j}
									className="border-b border-stone-100 px-4 py-3 text-stone-700"
								>
									{cell}
								</td>
							))}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	)
}

/**
 * CTA - Call to action block
 */
export function CTA({
	title,
	description,
	buttonText,
	buttonHref,
	variant = 'primary',
}: {
	title: string
	description?: string
	buttonText: string
	buttonHref: string
	variant?: 'primary' | 'secondary'
}) {
	const isPrimary = variant === 'primary'

	return (
		<div
			className={cn(
				'my-8 rounded-lg p-6 text-center',
				isPrimary ? 'bg-purple-50 border border-purple-100' : 'bg-stone-50 border border-stone-200',
			)}
		>
			<h4 className={cn('mb-2 font-medium', isPrimary ? 'text-purple-900' : 'text-stone-900')}>
				{title}
			</h4>
			{description && (
				<p className={cn('mb-4 text-sm', isPrimary ? 'text-purple-700' : 'text-stone-600')}>
					{description}
				</p>
			)}
			<a
				href={buttonHref}
				className={cn(
					'inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-medium transition-colors',
					isPrimary
						? 'bg-purple-600 text-white hover:bg-purple-700'
						: 'bg-stone-200 text-stone-700 hover:bg-stone-300',
				)}
			>
				{buttonText}
				<span aria-hidden="true">→</span>
			</a>
		</div>
	)
}

/**
 * Disclaimer - Medical/legal disclaimer block
 */
export function Disclaimer({ children }: { children: React.ReactNode }) {
	return (
		<div className="my-8 rounded-lg border border-stone-200 bg-stone-50 p-5">
			<div className="mb-2 text-xs font-medium uppercase tracking-wide text-stone-500">
				Disclaimer
			</div>
			<div className="text-sm leading-relaxed text-stone-600">{children}</div>
		</div>
	)
}
